import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type { DocumentReleaseDetails } from "~/api/types";
import {
  updateDocumentRelease,
  type ReleaseEditableField,
} from "~/api/v2/requests";
import { Button } from "~/atoms/button";
import { DialogFooter } from "~/atoms/dialog";
import { Form } from "~/atoms/form";
import { useDocumentReleaseEditForm } from "~/hooks/useDocumentReleaseEditForm";
import { ReleaseReviewDateFields } from "~/molecules/document-fields/ReleaseReviewDateFields";

interface ReleaseFieldEditFormProps {
  release: DocumentReleaseDetails;
  field: ReleaseEditableField;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}

export const ReleaseFieldEditForm = ({
  release,
  field,
  onCancel,
  onSaved,
}: ReleaseFieldEditFormProps) => {
  const { t } = useTranslation();
  const { form, filterUnchangedValues } = useDocumentReleaseEditForm({
    release,
    field,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = form.handleSubmit(
    async (values) => {
      const payload = filterUnchangedValues(values);
      if (Object.keys(payload).length === 0) {
        onCancel();
        return;
      }

      try {
        setIsSubmitting(true);
        await updateDocumentRelease(release.id, payload);
        toast.success(t(($) => $.toasts.success.documentUpdated));
        await onSaved();
        onCancel();
      } catch {
        toast.error(t(($) => $.toasts.error.generic));
      } finally {
        setIsSubmitting(false);
      }
    },
    (errors) => {
      const firstError = Object.values(errors).find((error) => error?.message);
      if (firstError?.message) {
        toast.error(firstError.message);
      } else {
        toast.error(t(($) => $.toasts.error.generic));
      }
    },
  );

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 pt-2">
        <ReleaseReviewDateFields field={field} />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            {t(($) => $.commons.buttons.cancel)}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {t(($) => $.commons.buttons.save)}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
